import React, { useEffect, useState } from 'react';
import { dashboardAPI } from '../services/api';
import { HealthData } from '../types';

const Health: React.FC = () => {
  const [data, setData] = useState<HealthData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const health = await dashboardAPI.getHealth();
      setData(health);
    } catch (error) {
      console.error('Failed to load health:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '40px' }}>Загрузка...</div>;
  if (!data) return <div style={{ textAlign: 'center', padding: '40px' }}>Нет данных</div>;

  return (
    <div>
      <h1>🏥 Здоровье</h1>
      
      {/* Under Development Banner */}
      <div style={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '40px',
        borderRadius: '12px',
        textAlign: 'center',
        marginBottom: '30px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
      }}>
        <div style={{ fontSize: '64px', marginBottom: '20px' }}>🚧</div>
        <h2 style={{ color: 'white', margin: '0 0 10px 0', fontSize: '28px' }}>
          Страница в разработке
        </h2>
        <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '16px', margin: 0 }}>
          Функционал "Здоровье" находится в стадии разработки и скоро будет доступен
        </p>
      </div>
      
      {/* Key Metrics */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '30px', opacity: 0.5, pointerEvents: 'none' }}>
        <div style={{ background: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', textAlign: 'center' }}>
          <h3 style={{ margin: '0 0 10px 0', color: '#666' }}>Вес</h3>
          <p style={{ fontSize: '32px', fontWeight: 'bold', color: '#3b82f6', margin: 0 }}>
            {data.weight} кг
          </p>
        </div>
        <div style={{ background: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', textAlign: 'center' }}>
          <h3 style={{ margin: '0 0 10px 0', color: '#666' }}>ИМТ</h3>
          <p style={{ fontSize: '32px', fontWeight: 'bold', color: '#16a34a', margin: 0 }}>
            {data.bmi}
          </p>
        </div>
        <div style={{ background: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', textAlign: 'center' }}>
          <h3 style={{ margin: '0 0 10px 0', color: '#666' }}>Пульс</h3>
          <p style={{ fontSize: '32px', fontWeight: 'bold', color: '#f59e0b', margin: 0 }}>
            {data.heartRate} уд/мин
          </p>
        </div>
        <div style={{ background: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', textAlign: 'center' }}>
          <h3 style={{ margin: '0 0 10px 0', color: '#666' }}>Давление</h3>
          <p style={{ fontSize: '32px', fontWeight: 'bold', color: '#8b5cf6', margin: 0 }}>
            {data.bloodPressure}
          </p>
        </div>
      </div>

      {/* Progress */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px', marginBottom: '30px' }}>
        <div style={{ background: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <h3>🚶 Шаги сегодня</h3>
          <div style={{ marginBottom: '10px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
              <span>{data.stepsToday} / {data.stepsGoal}</span>
              <span>{((data.stepsToday / data.stepsGoal) * 100).toFixed(0)}%</span>
            </div>
            <div style={{ width: '100%', height: '20px', background: '#e5e7eb', borderRadius: '10px', overflow: 'hidden' }}>
              <div style={{ 
                width: `${Math.min((data.stepsToday / data.stepsGoal) * 100, 100)}%`, 
                height: '100%', 
                background: 'linear-gradient(90deg, #3b82f6, #8b5cf6)',
                transition: 'width 0.3s'
              }} />
            </div>
          </div>
        </div>

        <div style={{ background: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <h3>😴 Сон</h3>
          <div style={{ marginBottom: '10px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
              <span>{data.sleepHours} / {data.sleepGoal} часов</span>
              <span>{((data.sleepHours / data.sleepGoal) * 100).toFixed(0)}%</span>
            </div>
            <div style={{ width: '100%', height: '20px', background: '#e5e7eb', borderRadius: '10px', overflow: 'hidden' }}>
              <div style={{ 
                width: `${Math.min((data.sleepHours / data.sleepGoal) * 100, 100)}%`, 
                height: '100%', 
                background: 'linear-gradient(90deg, #8b5cf6, #ec4899)',
                transition: 'width 0.3s'
              }} />
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activities */}
      <div style={{ background: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginBottom: '30px' }}>
        <h2>Недавняя активность</h2>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
              <th style={{ padding: '10px', textAlign: 'left' }}>Тип</th>
              <th style={{ padding: '10px', textAlign: 'left' }}>Дата</th>
              <th style={{ padding: '10px', textAlign: 'right' }}>Длительность</th>
              <th style={{ padding: '10px', textAlign: 'right' }}>Калории</th>
            </tr>
          </thead>
          <tbody>
            {data.recentActivities.map((activity) => (
              <tr key={activity.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                <td style={{ padding: '10px' }}>{activity.type}</td>
                <td style={{ padding: '10px' }}>{new Date(activity.date).toLocaleDateString('ru-RU')}</td>
                <td style={{ padding: '10px', textAlign: 'right' }}>{activity.duration} мин</td>
                <td style={{ padding: '10px', textAlign: 'right', fontWeight: 'bold', color: '#16a34a' }}>
                  {activity.calories} ккал
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Health Metrics */}
      <div style={{ background: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <h2>Цели здоровья</h2>
        {data.healthMetrics.map((metric) => {
          const percentage = (metric.current / metric.target) * 100;
          return (
            <div key={metric.metric} style={{ marginBottom: '15px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                <span style={{ fontWeight: '500' }}>{metric.metric}</span>
                <span style={{ color: '#666' }}>
                  {metric.current} / {metric.target} {metric.unit}
                </span>
              </div>
              <div style={{ width: '100%', height: '10px', background: '#e5e7eb', borderRadius: '5px', overflow: 'hidden' }}>
                <div style={{ 
                  width: `${Math.min(percentage, 100)}%`, 
                  height: '100%', 
                  background: percentage >= 100 ? '#16a34a' : percentage > 75 ? '#f59e0b' : '#3b82f6',
                  transition: 'width 0.3s'
                }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Health;
